import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CONVERSATION_SELECTION_QUERY_PARAM,
  CONVERSATION_SELECTION_SOURCES,
  type ConversationSelection,
  type ConversationSelectionSource,
  stashConversationSelection,
} from '@/entities/conversations';
import { appGeneratePath, openUrlInNewTab, useAppNavigate } from '@/shared/lib';

/**
 * Выбранный на дашборде элемент показателя: чем он подписан и какие разговоры за ним стоят.
 * Состав задаёт сервер — карточка отдаёт его как есть, ничего не пересчитывая.
 */
export type IndicatorItem = {
  /** Подпись элемента — станет описанием выборки и частью имени сохранённого пресета */
  label: string;
  /** Разговоры, из которых показатель выведен; пусто или не задано — переходить не к чему */
  conversationIds?: number[];
};

/** Обработчик выбора элемента показателя: `openInNewTab` — пользователь кликнул с shift. */
export type IndicatorSelectHandler = (item: IndicatorItem, openInNewTab: boolean) => void;

type Result = {
  /** Обработчик выбора для каждого источника выборки: карточка сообщает элемент, страница открывает список */
  selectHandlers: Record<ConversationSelectionSource, IndicatorSelectHandler>;
  /** Открывает один диалог по идентификатору разговора */
  openConversation: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Переходы дашборда к диалогам: к выборке показателя и к одному диалогу.
 *
 * Показателей на дашборде много, а различаются переходы только составом, описанием и признаком
 * источника, поэтому переход собирается одним способом на все карточки. Вкладку выбирает
 * пользователь: обычный клик ведёт в текущей, shift+клик открывает новую. Открытие идёт мимо
 * `window.open`: под shift браузер понимает его как «отдельное окно» — см. {@link openUrlInNewTab}.
 *
 * Выборка адреса не имеет: состав — сотни, а у критерия и тысячи идентификаторов. Поэтому в новую
 * вкладку она передаётся через хранилище браузера, а в адрес идёт лишь ключ к ней.
 */
export const useIndicatorTransitions = (): Result => {
  const navigate = useAppNavigate();
  const location = useLocation();

  const openSelection = useCallback(
    (selection: ConversationSelection, openInNewTab: boolean) => {
      if (openInNewTab) {
        const stashKey = stashConversationSelection(selection);
        const path = appGeneratePath('/dialogs/:id?', { id: null });
        openUrlInNewTab(`${path}?${CONVERSATION_SELECTION_QUERY_PARAM}=${stashKey}`);
        return;
      }
      navigate('/dialogs/:id?', { id: null }, { state: selection });
    },
    [navigate],
  );

  const openConversation = useCallback(
    (conversationId: number, openInNewTab: boolean) => {
      const id = String(conversationId);
      if (openInNewTab) {
        openUrlInNewTab(appGeneratePath('/dialogs/:id', { id }));
        return;
      }
      // Источник перехода — текущий интерфейс (дашборд): «Назад» в карточке диалога
      // вернёт сюда, а не в общий список диалогов.
      navigate('/dialogs/:id', { id }, { state: `${location.pathname}${location.search}` });
    },
    [navigate, location.pathname, location.search],
  );

  // Показатель без диалогов не кликабелен, но проверка состава живёт и здесь: пустой состав
  // открыл бы список с фильтром, не находящим ничего.
  const createSelectHandler = useCallback(
    (source: ConversationSelectionSource): IndicatorSelectHandler =>
      (item, openInNewTab) => {
        if (!item.conversationIds?.length) return;
        openSelection({ conversationIds: item.conversationIds, description: item.label, source }, openInNewTab);
      },
    [openSelection],
  );

  const selectHandlers = useMemo(
    () =>
      Object.fromEntries(
        CONVERSATION_SELECTION_SOURCES.map((source) => [source, createSelectHandler(source)]),
      ) as Record<ConversationSelectionSource, IndicatorSelectHandler>,
    [createSelectHandler],
  );

  return { selectHandlers, openConversation };
};
